import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  Box,
  Typography,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Paper,
  IconButton,
} from '@mui/material';
import {
  FileDownload as DownloadIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  FilterList as FilterIcon,
  ViewColumn as ColumnIcon,
} from '@mui/icons-material';
import publicApplicationsService from '../services/publicApplicationsService';

const ExportApplicationsModal = ({ open, onClose, currentFilters, jobs }) => {
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [availableColumns, setAvailableColumns] = useState({});
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [filters, setFilters] = useState({
    status: '',
    job_id: '',
    source: '',
    from_date: '',
    to_date: '',
  });

  // Load available columns when modal opens
  useEffect(() => {
    if (open) {
      loadExportColumns();
      // Apply current filters from the main page
      if (currentFilters) {
        setFilters({
          status: currentFilters.status || '',
          job_id: currentFilters.job_id || '',
          source: currentFilters.source || '',
          from_date: currentFilters.from_date || '',
          to_date: currentFilters.to_date || '',
        });
      }
    }
  }, [open, currentFilters]);

  const loadExportColumns = async () => {
    try {
      setLoading(true);
      const response = await publicApplicationsService.getExportColumns();
      if (response.success) {
        setAvailableColumns(response.columns);
        setSelectedColumns([]);
      }
    } catch (error) {
      console.error('Error loading export columns:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleColumnToggle = (column) => {
    setSelectedColumns((prev) => {
      if (prev.includes(column)) {
        return prev.filter((c) => c !== column);
      } else {
        return [...prev, column];
      }
    });
  };

  const handleSelectCategory = (category) => {
    const categoryColumns = availableColumns[category] || [];
    const allSelected = categoryColumns.every((col) => selectedColumns.includes(col.key));

    if (allSelected) {
      // Deselect all in this category
      setSelectedColumns((prev) => prev.filter((c) => !categoryColumns.find((col) => col.key === c)));
    } else {
      // Select all in this category
      const newColumns = categoryColumns.map((col) => col.key);
      setSelectedColumns((prev) => [...new Set([...prev, ...newColumns])]);
    }
  };

  const handleExport = async () => {
    if (selectedColumns.length === 0) {
      alert('Please select at least one column to export');
      return;
    }

    try {
      setExportLoading(true);
      await publicApplicationsService.exportApplications({
        columns: selectedColumns,
        filters: {
          status: filters.status || undefined,
          job_id: filters.job_id || undefined,
          source: filters.source || undefined,
          from_date: filters.from_date || undefined,
          to_date: filters.to_date || undefined,
        },
      });
      onClose();
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const categoryLabels = {
    basic: 'Basic Information',
    personal: 'Personal Details',
    application: 'Application Info',
    professional: 'Professional Info',
    education: 'Education',
  };

  const categoryIcons = {
    basic: '👤',
    personal: '📋',
    application: '📝',
    professional: '💼',
    education: '🎓',
  };

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'under_review', label: 'Under Review' },
    { value: 'shortlisted', label: 'Shortlisted' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'on_hold', label: 'On Hold' },
  ];

  const sourceOptions = [
    { value: '', label: 'All Sources' },
    { value: 'public_form', label: 'Public Form' },
    { value: 'manual_entry', label: 'Manual Entry' },
    { value: 'imported', label: 'Imported' },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ bgcolor: '#29204C', color: 'white', pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <DownloadIcon sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Export Applications to Excel
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Select columns and apply filters to export
              </Typography>
            </Box>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress size={50} />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Filters Section */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2.5, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <FilterIcon sx={{ color: '#29204C' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#29204C' }}>
                    Filters
                  </Typography>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        label="Status"
                      >
                        {statusOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Job Position</InputLabel>
                      <Select
                        value={filters.job_id}
                        onChange={(e) => setFilters({ ...filters, job_id: e.target.value })}
                        label="Job Position"
                      >
                        <MenuItem value="">All Positions</MenuItem>
                        {jobs.map((job) => (
                          <MenuItem key={job.job_id} value={job.job_id}>
                            {job.job_title || job.posting_title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Source</InputLabel>
                      <Select
                        value={filters.source}
                        onChange={(e) => setFilters({ ...filters, source: e.target.value })}
                        label="Source"
                      >
                        {sourceOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="From Date"
                      type="date"
                      value={filters.from_date}
                      onChange={(e) => setFilters({ ...filters, from_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="To Date"
                      type="date"
                      value={filters.to_date}
                      onChange={(e) => setFilters({ ...filters, to_date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Columns Selection */}
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ColumnIcon sx={{ color: '#29204C' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: '#29204C' }}>
                    Select Columns
                  </Typography>
                </Stack>
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`${selectedColumns.length} selected`}
                  color={selectedColumns.length > 0 ? 'success' : 'default'}
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>

              <Alert severity="info" icon={<DownloadIcon />} sx={{ mb: 2.5 }}>
                <strong>Note:</strong> Default columns (Candidate ID, Full Name, Email, Phone) will always be included in the export.
              </Alert>

              {Object.entries(availableColumns).map(([category, columns]) => (
                <Paper
                  key={category}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: '1px solid #e0e0e0',
                    '&:hover': { boxShadow: 2 },
                    transition: 'box-shadow 0.2s',
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 20 }}>
                        {categoryIcons[category]}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#29204C' }}>
                        {categoryLabels[category] || category}
                      </Typography>
                      <Chip
                        label={`${columns.filter(col => selectedColumns.includes(col.key)).length}/${columns.length}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Stack>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleSelectCategory(category)}
                      sx={{
                        textTransform: 'none',
                        borderColor: '#29204C',
                        color: '#29204C',
                        '&:hover': { borderColor: '#1a1430', bgcolor: '#f5f5f5' },
                      }}
                    >
                      {columns.every((col) => selectedColumns.includes(col.key))
                        ? 'Deselect All'
                        : 'Select All'}
                    </Button>
                  </Stack>

                  <FormGroup row>
                    {columns.map((column) => (
                      <FormControlLabel
                        key={column.key}
                        control={
                          <Checkbox
                            checked={selectedColumns.includes(column.key)}
                            onChange={() => handleColumnToggle(column.key)}
                            size="small"
                            sx={{
                              color: '#29204C',
                              '&.Mui-checked': { color: '#29204C' },
                            }}
                          />
                        }
                        label={column.label}
                        sx={{
                          minWidth: '220px',
                          mb: 0.5,
                          '& .MuiFormControlLabel-label': {
                            fontSize: '0.875rem',
                          },
                        }}
                      />
                    ))}
                  </FormGroup>
                </Paper>
              ))}
            </Grid>
          </Grid>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#f8f9fa', gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={exportLoading}
          sx={{
            borderColor: '#666',
            color: '#666',
            '&:hover': { borderColor: '#333', bgcolor: 'white' },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          startIcon={exportLoading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
          disabled={loading || exportLoading || selectedColumns.length === 0}
          sx={{
            bgcolor: '#2e7d32',
            px: 3,
            '&:hover': { bgcolor: '#1b5e20' },
            '&:disabled': { bgcolor: '#ccc' },
          }}
        >
          {exportLoading ? 'Exporting...' : 'Export to Excel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExportApplicationsModal;
